import { Education } from '@applicator/shared'
import React, { useState, useEffect, useRef, useCallback } from 'react'

import CustomCheckbox from '../CustomCheckbox'

import EducationItem from './EducationItem'
import EducationPortals from './EducationPortals'
import ResumeSectionEmptyHint from './ResumeSectionEmptyHint'
import { createDatePickerPosition } from './useWorkExperienceRoleHandlers/datePickerHelpers'
import { useDatePickerAnchorPosition } from './useDatePickerAnchorPosition'

export default function EducationEditor({ 
  data, 
  onChange,
  onAddEducationRef,
  onFocusEducationEntry,
}: { 
  data: Education[]
  onChange: (data: Education[]) => void
  onAddEducationRef?: (fn: () => void) => void
  /** Keep education visible and scroll to the entry after a field include toggle. */
  onFocusEducationEntry?: (educationId: string) => void
}) {
  const educations = Array.isArray(data) ? data : []
  const [editingInstitutionId, setEditingInstitutionId] = useState<string | null>(null)
  const [editingLocationId, setEditingLocationId] = useState<string | null>(null)
  const [editingDegreeId, setEditingDegreeId] = useState<string | null>(null)
  const [editingFieldOfStudyId, setEditingFieldOfStudyId] = useState<string | null>(null)
  const [editingAdditionalInfoId, setEditingAdditionalInfoId] = useState<string | null>(null)
  const [editingInstitution, setEditingInstitution] = useState('')
  const [editingLocation, setEditingLocation] = useState('')
  const [editingDegree, setEditingDegree] = useState('')
  const [editingFieldOfStudy, setEditingFieldOfStudy] = useState('')
  const [editingAdditionalInfo, setEditingAdditionalInfo] = useState('')
  const additionalInfoTextareaRef = useRef<HTMLTextAreaElement | null>(null)
  const [datePickerState, setDatePickerState] = useState<{
    educationId: string
    field: 'startDate' | 'endDate'
    position: { top: number; left: number; width: number }
    triggerKey: string
  } | null>(null)
  const datePickerRef = useRef<HTMLDivElement>(null)
  const dateFieldRefs = useRef<{ [key: string]: HTMLElement | null }>({})
  const [editingStartDate, setEditingStartDate] = useState('')
  const [editingEndDate, setEditingEndDate] = useState('')
  const [hoveredEducationId, setHoveredEducationId] = useState<string | null>(null)

  const handleAddEducation = useCallback(() => {
    const newEducation: Education = {
      id: `education-${Date.now()}`,
      institution: '',
      location: '',
      degree: '',
      fieldOfStudy: '',
      startDate: '',
      endDate: '',
      additionalInformation: '',
      included: false, // Disabled by default until institution is filled
      locationIncluded: false,
      degreeIncluded: false,
      startDateIncluded: false,
      endDateIncluded: false,
      additionalInformationIncluded: false,
    }
    onChange([...educations, newEducation])
    setTimeout(() => {
      setEditingInstitutionId(newEducation.id)
      setEditingInstitution('')
    }, 0)
  }, [educations, onChange])

  // Expose handleAddEducation to parent via ref
  useEffect(() => {
    if (onAddEducationRef) {
      onAddEducationRef(handleAddEducation)
    }
    return () => {
      if (onAddEducationRef) {
        onAddEducationRef(() => {}) // Cleanup: set empty function
      }
    }
  }, [handleAddEducation, onAddEducationRef])

  const handleDeleteEducation = (educationId: string) => {
    onChange(educations.filter((edu) => edu.id !== educationId))
  }

  const handleUpdateEducation = (
    educationId: string,
    field: 'institution' | 'location' | 'degree' | 'fieldOfStudy' | 'startDate' | 'endDate' | 'additionalInformation',
    value: string
  ) => {
    const updated = educations.map((edu) =>
      edu.id === educationId ? { ...edu, [field]: value } : edu
    )
    onChange(updated)
  }

  const handleToggleEducation = (educationId: string) => {
    const updated = educations.map((edu) => {
      if (edu.id === educationId) {
        const newIncluded = !(edu.included ?? false)
        // If enabling institution, also enable all other checkboxes
        if (newIncluded) {
          return {
            ...edu,
            included: true,
            locationIncluded: true,
            degreeIncluded: true,
            startDateIncluded: true,
            endDateIncluded: true,
            additionalInformationIncluded: true,
          }
        } else {
          // If disabling institution, also disable all other checkboxes
          return {
            ...edu,
            included: false,
            locationIncluded: false,
            degreeIncluded: false,
            startDateIncluded: false,
            endDateIncluded: false,
            additionalInformationIncluded: false,
          }
        }
      }
      return edu
    })
    onChange(updated)
  }

  const handleToggleLocation = (educationId: string) => {
    const updated = educations.map((edu) =>
      edu.id === educationId ? { ...edu, locationIncluded: !(edu.locationIncluded ?? false) } : edu
    )
    onChange(updated)
    onFocusEducationEntry?.(educationId)
  }

  const handleToggleDegree = (educationId: string) => {
    const updated = educations.map((edu) =>
      edu.id === educationId ? { ...edu, degreeIncluded: !(edu.degreeIncluded ?? false) } : edu
    )
    onChange(updated)
  }

  const handleToggleStartDate = (educationId: string) => {
    const updated = educations.map((edu) =>
      edu.id === educationId ? { ...edu, startDateIncluded: !(edu.startDateIncluded ?? false) } : edu
    )
    onChange(updated)
  }

  const handleToggleEndDate = (educationId: string) => {
    const updated = educations.map((edu) =>
      edu.id === educationId ? { ...edu, endDateIncluded: !(edu.endDateIncluded ?? false) } : edu
    )
    onChange(updated)
  }

  const handleToggleAdditionalInformation = (educationId: string) => {
    const updated = educations.map((edu) =>
      edu.id === educationId ? { ...edu, additionalInformationIncluded: !(edu.additionalInformationIncluded ?? false) } : edu
    )
    onChange(updated)
  }

  const handleStartEditInstitution = (education: Education) => {
    setEditingInstitutionId(education.id)
    setEditingInstitution(education.institution || '')
  }

  const handleSaveInstitution = (educationId: string) => {
    const trimmedValue = editingInstitution.trim()
    // Update institution and checkboxes in one call
    const updated = educations.map((edu) => {
      if (edu.id === educationId) {
        if (trimmedValue) {
          // Auto-enable checkboxes if institution is filled
          return {
            ...edu,
            institution: trimmedValue,
            included: true,
            locationIncluded: true,
            degreeIncluded: true,
            startDateIncluded: true,
            endDateIncluded: true,
            additionalInformationIncluded: true
          }
        } else {
          // Auto-disable checkboxes if institution is empty
          return {
            ...edu,
            institution: trimmedValue,
            included: false,
            locationIncluded: false,
            degreeIncluded: false,
            startDateIncluded: false,
            endDateIncluded: false,
            additionalInformationIncluded: false
          }
        }
      }
      return edu
    })
    onChange(updated)
    setEditingInstitutionId(null)
  }

  const handleStartEditLocation = (education: Education) => {
    setEditingLocationId(education.id)
    setEditingLocation(education.location || '')
  }

  const handleSaveLocation = (educationId: string) => {
    handleUpdateEducation(educationId, 'location', editingLocation)
    setEditingLocationId(null)
  }

  const handleStartEditDegree = (education: Education) => {
    setEditingDegreeId(education.id)
    setEditingDegree(education.degree || '')
  }

  const handleSaveDegree = (educationId: string) => {
    handleUpdateEducation(educationId, 'degree', editingDegree)
    setEditingDegreeId(null)
  }

  const handleStartEditFieldOfStudy = (education: Education) => {
    setEditingFieldOfStudyId(education.id)
    setEditingFieldOfStudy(education.fieldOfStudy || '')
  }

  const handleSaveFieldOfStudy = (educationId: string) => {
    handleUpdateEducation(educationId, 'fieldOfStudy', editingFieldOfStudy)
    setEditingFieldOfStudyId(null)
  }

  const handleStartEditAdditionalInfo = (education: Education) => {
    setEditingAdditionalInfoId(education.id)
    setEditingAdditionalInfo(education.additionalInformation || '')
  }

  const handleSaveAdditionalInfo = (educationId: string) => {
    handleUpdateEducation(educationId, 'additionalInformation', editingAdditionalInfo)
    setEditingAdditionalInfoId(null)
  }

  const handleStartEditStartDate = (education: Education, event: React.MouseEvent) => {
    event.stopPropagation()
    const triggerElement = event.currentTarget as HTMLElement
    const triggerKey = `${education.id}-startDate`
    dateFieldRefs.current[triggerKey] = triggerElement
    setDatePickerState({
      educationId: education.id,
      field: 'startDate',
      position: createDatePickerPosition(triggerElement, { monthYearOnly: true }),
      triggerKey,
    })
    setEditingStartDate(education.startDate || '')
  }

  const handleStartEditEndDate = (education: Education, event: React.MouseEvent) => {
    event.stopPropagation()
    const triggerElement = event.currentTarget as HTMLElement
    const triggerKey = `${education.id}-endDate`
    dateFieldRefs.current[triggerKey] = triggerElement
    setDatePickerState({
      educationId: education.id,
      field: 'endDate',
      position: createDatePickerPosition(triggerElement, { monthYearOnly: true }),
      triggerKey,
    })
    setEditingEndDate(education.endDate || '')
  }

  const handleDateChange = (value: string) => {
    if (datePickerState) {
      handleUpdateEducation(datePickerState.educationId, datePickerState.field, value)
      if (datePickerState.field === 'startDate') {
        setEditingStartDate(value)
      } else {
        setEditingEndDate(value)
      }
    }
  }

  // Auto-resize textarea for additional information
  const adjustTextareaHeight = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = 'auto'
    textarea.style.height = `${textarea.scrollHeight}px`
  }

  const handleAdditionalInfoChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditingAdditionalInfo(e.target.value)
    adjustTextareaHeight(e.target)
  }

  useEffect(() => {
    if (editingAdditionalInfoId && additionalInfoTextareaRef.current) {
      adjustTextareaHeight(additionalInfoTextareaRef.current)
    }
  }, [editingAdditionalInfoId])

  // Helper function to render checkbox
  const renderCheckbox = (checked: boolean, onChange: () => void) => {
    return (
      <CustomCheckbox
        checked={checked}
        onChange={onChange}
        size="xs"
        className="flex-shrink-0"
      />
    )
  }

  // Close date picker on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerState && datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement
        const triggerKey = datePickerState.triggerKey
        const triggerElement = dateFieldRefs.current[triggerKey]
        if (triggerElement && !triggerElement.contains(target)) {
          setDatePickerState(null)
        }
      }
    }

    if (datePickerState) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [datePickerState])

  useDatePickerAnchorPosition(
    datePickerState,
    dateFieldRefs,
    setDatePickerState,
    datePickerRef,
    { monthYearOnly: true },
  )

  // Prepare props for EducationItem
  const educationItemProps = {
    editingInstitutionId,
    editingLocationId,
    editingDegreeId,
    editingFieldOfStudyId,
    editingAdditionalInfoId,
    editingInstitution,
    editingLocation,
    editingDegree,
    editingFieldOfStudy,
    editingAdditionalInfo,
    hoveredEducationId,
    additionalInfoTextareaRef,
    dateFieldRefs,
    onStartEditInstitution: handleStartEditInstitution,
    onSaveInstitution: handleSaveInstitution,
    onStartEditLocation: handleStartEditLocation,
    onSaveLocation: handleSaveLocation,
    onStartEditDegree: handleStartEditDegree,
    onSaveDegree: handleSaveDegree,
    onStartEditFieldOfStudy: handleStartEditFieldOfStudy,
    onSaveFieldOfStudy: handleSaveFieldOfStudy,
    onStartEditAdditionalInfo: handleStartEditAdditionalInfo,
    onSaveAdditionalInfo: handleSaveAdditionalInfo,
    onDeleteEducation: handleDeleteEducation,
    onToggleEducation: handleToggleEducation,
    onToggleLocation: handleToggleLocation,
    onToggleDegree: handleToggleDegree,
    onToggleStartDate: handleToggleStartDate,
    onToggleEndDate: handleToggleEndDate,
    onToggleAdditionalInformation: handleToggleAdditionalInformation,
    onStartEditStartDate: handleStartEditStartDate,
    onStartEditEndDate: handleStartEditEndDate,
    setHoveredEducationId,
    setEditingInstitution,
    setEditingLocation,
    setEditingDegree,
    setEditingFieldOfStudy,
    setEditingAdditionalInfo,
    setEditingInstitutionId,
    setEditingLocationId,
    setEditingDegreeId,
    setEditingFieldOfStudyId,
    setEditingAdditionalInfoId,
    adjustTextareaHeight,
    handleAdditionalInfoChange,
    renderCheckbox,
  }

  return (
    <div className="space-y-4 pl-6">
      {educations.length === 0 && <ResumeSectionEmptyHint sectionType="education" />}

      {educations.map((education) => (
        <EducationItem
          key={education.id}
          education={education}
          {...educationItemProps}
        />
      ))}
      <button
        onClick={handleAddEducation}
        className="text-sm text-interactive hover:text-interactive font-medium"
      >
        + Add Education
      </button>

      <EducationPortals
        datePickerState={datePickerState}
        datePickerRef={datePickerRef}
        editingStartDate={editingStartDate}
        editingEndDate={editingEndDate}
        onDateChange={handleDateChange}
        onClose={() => setDatePickerState(null)}
      />
    </div>
  )
}